import React, { Fragment, useMemo } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Table, Spinner, Badge, Alert } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetStocktakeByIdQuery } from '../../../../store/services/inventoryApi';
import { ArrowLeft, Printer, CheckCircle } from 'react-feather';

const StocktakeDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data: stocktakeResponse, isLoading } = useGetStocktakeByIdQuery(id);

    const stocktake = stocktakeResponse?.data || stocktakeResponse;

    const stats = useMemo(() => {
        if (!stocktake?.items) return { total: 0, matched: 0, variance: 0, gain: 0, loss: 0 };

        let matched = 0, gain = 0, loss = 0;
        stocktake.items.forEach(item => {
            const v = item.countedQty - item.systemQty;
            if (v === 0) matched++;
            else if (v > 0) gain++;
            else loss++;
        });

        return {
            total: stocktake.items.length,
            matched,
            gain,
            loss,
            variance: gain + loss
        };
    }, [stocktake]);

    if (isLoading) return <div className="text-center p-5"><Spinner color="primary" /></div>;
    if (!stocktake) return <div className="text-center p-5">Stocktake session not found</div>;

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Stocktake Details" parent="Inventory" title={`ST-${stocktake.stocktakeNumber || id.slice(-6)}`} />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5>Stocktake #{stocktake.stocktakeNumber}</h5>
                                    <Badge color={stocktake.status === 'Completed' ? 'success' : 'warning'}>{stocktake.status}</Badge>
                                    <Badge color="light" className="text-dark ms-2">{stocktake.type}</Badge>
                                </div>
                                <div className="d-flex gap-2 d-print-none">
                                    <Button color="light" onClick={() => navigate('/inventory/stocktakes')}>
                                        <ArrowLeft size={14} className="me-1" /> Back
                                    </Button>
                                    <Button color="secondary" onClick={() => window.print()}>
                                        <Printer size={14} className="me-1" /> Print Report
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <Row className="mb-4">
                                    <Col md="6">
                                        <h6 className="text-primary border-bottom pb-2">Store Information</h6>
                                        <p className="mb-1"><strong>Name:</strong> {stocktake.storeId?.storeName}</p>
                                        <p className="mb-1"><strong>Code:</strong> {stocktake.storeId?.storeNumber}</p>
                                        <p className="mb-1"><strong>Manager:</strong> {stocktake.storeId?.storeManager}</p>
                                    </Col>
                                    <Col md="6">
                                        <h6 className="text-primary border-bottom pb-2">Session Details</h6>
                                        <p className="mb-1"><strong>Started At:</strong> {new Date(stocktake.createdAt).toLocaleString()}</p>
                                        {stocktake.completedAt && (
                                            <p className="mb-1"><strong>Completed At:</strong> {new Date(stocktake.completedAt).toLocaleString()}</p>
                                        )}
                                        <p className="mb-1"><strong>Notes:</strong> {stocktake.notes || 'N/A'}</p>
                                    </Col>
                                </Row>

                                {stocktake.status === 'Completed' && (
                                    <Alert color="success" className="d-flex align-items-center mb-4">
                                        <CheckCircle size={18} className="me-2" />
                                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;This stocktake has been finalized. System quantities were adjusted to match physical counts.
                                    </Alert>
                                )}

                                <Row className="mb-4 text-center">
                                    <Col md="3">
                                        <Card className="bg-light shadow-none">
                                            <CardBody className="p-3">
                                                <h6 className="text-muted small">Total SKUs</h6>
                                                <h4 className="mb-0">{stats.total}</h4>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md="3">
                                        <Card className="bg-light shadow-none">
                                            <CardBody className="p-3">
                                                <h6 className="text-muted small">Matched</h6>
                                                <h4 className="mb-0 text-success">{stats.matched}</h4>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md="3">
                                        <Card className="bg-light shadow-none">
                                            <CardBody className="p-3">
                                                <h6 className="text-muted small">Gains (+)</h6>
                                                <h4 className="mb-0 text-info">{stats.gain}</h4>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md="3">
                                        <Card className="bg-light shadow-none">
                                            <CardBody className="p-3">
                                                <h6 className="text-muted small">Losses (-)</h6>
                                                <h4 className="mb-0 text-danger">{stats.loss}</h4>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                                <Table responsive bordered striped>
                                    <thead className="table-light text-center">
                                        <tr>
                                            <th>#</th>
                                            <th className="text-start">Product Variant</th>
                                            <th>SKU</th>
                                            <th>Snapshot Qty</th>
                                            <th>Counted Qty</th>
                                            <th>Variance</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-center align-middle">
                                        {stocktake.items?.map((item, index) => {
                                            const variance = item.countedQty - item.systemQty;
                                            return (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td className="text-start">
                                                        <div className="fw-bold">{item.productVariantId?.productName}</div>
                                                        <small className="text-muted">{item.productVariantId?.variantName}</small>
                                                    </td>
                                                    <td>{item.productVariantId?.sku}</td>
                                                    <td>{item.systemQty}</td>
                                                    <td className="fw-bold">{item.countedQty}</td>
                                                    <td className={`fw-bold ${variance === 0 ? 'text-success' : variance > 0 ? 'text-info' : 'text-danger'}`}>
                                                        {variance > 0 ? `+${variance}` : variance}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default StocktakeDetails;
